import { FeatureId, Features } from '@weavix/models/src/features/feature';
import { max, maxBy, partition } from 'lodash';

/**
 * A persisted feature.
 */
export interface DomainFeature {
    /**
     * The persisted {@link FeatureId}.
     */
    id: string;
    /**
     * Default value of the feature.
     * `null` or `undefined` is interpreted as false.
     */
    defaultValue?: boolean;

    /** Last time this value was updated */
    lastUpdated?: Date;
}

/**
 * This indicates if a feature flag is explicitly set for a site or account.
 * An `enabled` value of `undefined` does NOT signal false;
 * it will signify that the flag value is unset for this site/account.
 * If the value is true, the contributors object will contain the facilityIds that have the flag enabled.
 */
export interface DomainFeatureFlag {
    accountId: string;
    facilityId?: string;
    enabled?: boolean;
    /** Last time the feature flag was updated */
    lastUpdated?: Date | string | number;
}

/**
 * The result of the feature flag check.
 */
export interface FeatureFlagEnabledResult {
    /** Aggregate enabled value, based on all inputs. */
    enabled: boolean;
    /** Aggregate last updated, based on all inputs. */
    lastUpdated?: Date | string | number;
    /** The inputs that contributed to this result. */
    contributors: {
        /** The site-specific settings that contributed, if any. */
        facilityIds?: string[];
        /** Whether the default value contributed (all flags have unset values). */
        default?: boolean;
        /** Whether the fallback value contributed (feature is unknown). */
        fallback?: boolean;
        /** Whether an account-wide setting contributed. */
        accountSetting?: boolean;
    };
}

/**
 * Returns the enabled value of a feature flag for the provided parameters.
 * If the feature is unknown, the fallback value is returned.
 */
export function featureFlagIsEnabled(
    featureId: FeatureId,
    feature: DomainFeature,
    featureFlags: DomainFeatureFlag[],
    facilityIds: string[],
    fallback: boolean,
): FeatureFlagEnabledResult {
    if (!feature || !Features[feature.id as FeatureId]) {
        console.warn(`Feature "${featureId}" is unknown, using fallback "${fallback}". Is it deployed and syncing?`);
        return {
            enabled: fallback,
            contributors: {
                fallback: true,
            },
        };
    }

    // For all flags targeting a site or account that the caller provided,
    // return true/false if any flags have a value for `enabled`.
    const matchingFlags = featureFlags?.filter(x => (x.facilityId === undefined || facilityIds.includes(x.facilityId)));
    const [setFlags, unsetFlags] = partition(matchingFlags, x => x.enabled !== undefined);
    if (setFlags?.length) {
        const contributors = setFlags.filter(x => x.enabled);
        const lastUpdated = maxBy(setFlags, x => x.lastUpdated ? new Date(x.lastUpdated).getTime() : 0)?.lastUpdated;

        return {
            enabled: contributors.length > 0,
            lastUpdated,
            contributors: {
                accountSetting: contributors.some(x => x.facilityId === undefined),
                facilityIds: setFlags.map(x => x.facilityId).filter(x => x !== undefined),
            },
        };
    }

    // lastUpdated is the most recent of either the feature default or any unset flags.
    const lastUnsetUpdated = maxBy(unsetFlags, x => x.lastUpdated ? new Date(x.lastUpdated).getTime() : 0)?.lastUpdated;
    const defaultLastUpdated = max([feature.lastUpdated, lastUnsetUpdated]);
    return {
        enabled: feature.defaultValue ?? false,
        lastUpdated: defaultLastUpdated,
        contributors: {
            default: true,
        },
    };
}
